// Tiny markdown editor with toolbar buttons that insert syntax + live preview.
// Body is markdown; rendered with react-markdown on public pages.
import React, { useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Bold, Italic, Heading2, Heading3, Link as LinkIcon, List, ListOrdered, Quote, Code, Image as ImageIcon,
} from "lucide-react";

const BTNS = [
  { icon: Heading2, before: "## ", after: "", title: "Heading 2" },
  { icon: Heading3, before: "### ", after: "", title: "Heading 3" },
  { icon: Bold, before: "**", after: "**", title: "Bold" },
  { icon: Italic, before: "*", after: "*", title: "Italic" },
  { icon: LinkIcon, before: "[", after: "](https://)", title: "Link" },
  { icon: List, before: "- ", after: "", title: "Bullet list" },
  { icon: ListOrdered, before: "1. ", after: "", title: "Numbered list" },
  { icon: Quote, before: "> ", after: "", title: "Blockquote" },
  { icon: Code, before: "`", after: "`", title: "Inline code" },
  { icon: ImageIcon, before: "![alt](", after: ")", title: "Image" },
];

export default function MarkdownEditor({ value, onChange, rows = 18, testId = "markdown-editor" }) {
  const ref = useRef(null);

  const wrap = (before, after) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const sel = (value || "").slice(start, end);
    const newText = (value || "").slice(0, start) + before + sel + after + (value || "").slice(end);
    onChange(newText);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + before.length, start + before.length + sel.length);
    }, 0);
  };

  return (
    <div className="rounded-md border border-ridge-border bg-white" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 border-b border-ridge-border px-2 py-2">
        {BTNS.map(({ icon: Icon, before, after, title }) => (
          <button
            key={title}
            type="button"
            title={title}
            aria-label={title}
            onClick={() => wrap(before, after)}
            className="inline-flex h-8 w-8 items-center justify-center rounded text-ridge-charcoal hover:bg-ridge-stone"
          >
            <Icon className="h-4 w-4" />
          </button>
        ))}
        <span className="ml-auto pr-2 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ridge-muted">
          Markdown · live preview below
        </span>
      </div>
      <textarea
        ref={ref}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        spellCheck="true"
        className="block w-full resize-y rounded-b-md border-0 px-4 py-3 font-mono text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ridge-charcoal/15"
        data-testid={`${testId}-textarea`}
      />
      {value ? (
        <div className="border-t border-ridge-border bg-ridge-stone/40 p-5">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ridge-muted">
            Preview
          </span>
          <article className="prose-ridge mt-3 max-w-none text-base leading-[1.8] text-ridge-text">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          </article>
        </div>
      ) : null}
    </div>
  );
}
